import { ExtractErrorInfo } from "components/shared/ExtractErrorInfo";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "Config/Api";
import { API_ENDPOINTS } from "Config/apiConfig";
import { AppType } from "types/appType";
import { IncidentCreateType } from "types/incidentCreateType";
import { ErrorType } from "types/errorType";
import { RootState } from "store/store";
import { Status } from "Config/Enum/statusEnum";

export interface AppTypeWithIncidentCreateType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  incident: IncidentCreateType;
  isFetching: boolean;
}

const initialState: AppTypeWithIncidentCreateType = {
  loading: false,
  error: null,
  incident: {
    reference: "",
    asset: null,
    voRScenarios: [],
    otherScenarios: [],
    contacts: [],
    repairers: [],
    jobsheets: [],
    client: {
      name: "",
      reference: "",
    },
  },
  isFetching: false,
};

interface SaveActionTypeParams {
  formData: IncidentCreateType;
}

// Thunk to handle both creating and editing action types
export const saveIncident = createAsyncThunk<{ reference: string; incidentId: string }, SaveActionTypeParams>(
  "incident/saveIncident",
  async ({ formData }, { rejectWithValue }) => {
    console.log("Saving incident with data:", formData);
    try {
      const response = await api.post<IncidentCreateType>(`${API_ENDPOINTS.incidents}`, formData);

      // Retrieve the Location header from the response
      const locationHeader = response.headers["location"];

      // Extract the ID from the Location URL
      const incidentId = locationHeader ? locationHeader.split("/").pop() : null;
      if (!response.data.reference || !incidentId) {
        throw new Error("Incident ID or reference is missing in the response.");
      }

      return { reference: response.data.reference, incidentId };
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

export const fetchIncident = createAsyncThunk<IncidentCreateType, string>(
  "incident/fetchIncident",
  async (incidentId, { rejectWithValue }) => {
    try {
      const response = await api.get<IncidentCreateType>(`${API_ENDPOINTS.incidents}/${incidentId}`);

      return response.data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

export const updateIncident = createAsyncThunk<IncidentCreateType, { status: Status; incidentId: string }>(
  "incident/updateIncident",
  async ({ status, incidentId }, { rejectWithValue }) => {
    try {
      const response = await api.patch<IncidentCreateType>(`${API_ENDPOINTS.incidents}/${incidentId}/status`, {
        status,
      });
      return response.data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

const incidentSlice = createSlice({
  name: "incident",
  initialState,
  reducers: {
    clearIncidentForm: (state) => {
      state.incident = initialState.incident;
      state.error = null;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveIncident.fulfilled, (state, action) => {
      return {
        ...state,
        error: null,
        loading: false,
      };
    });
    builder.addCase(saveIncident.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(saveIncident.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    builder.addCase(fetchIncident.fulfilled, (_, action) => {
      // Order repairers alphabetically by name (case-insensitive) without mutating payload
      let sortedRepairers = action.payload.repairers
        ? [...action.payload.repairers].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        : [];

      return {
        incident: {
          ...action.payload,
          repairers: sortedRepairers,
        },
        error: null,
        loading: false,
        isFetching: false,
      };
    });
    builder.addCase(fetchIncident.pending, (state) => {
      state.loading = true;
      state.isFetching = true;
    });
    builder.addCase(fetchIncident.rejected, (state, action) => {
      state.loading = false;
      state.isFetching = false;
      state.error = action.payload as ErrorType;
    });
    builder.addCase(updateIncident.fulfilled, (state, action) => {
      return {
        incident: action.payload,
        error: null,
        loading: false,
        isFetching: false,
      };
    });
    builder.addCase(updateIncident.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateIncident.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
  },
});

export const { clearIncidentForm, clearError } = incidentSlice.actions;

export const incidentRootState = (state: RootState) => state;
export default incidentSlice.reducer;
